import { Box, IconButton, Tooltip } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { useParagraphAudio } from '../../api/queries/audio';
import { AudioPlayer } from '../common/AudioPlayer';

interface ParagraphAudioIndicatorProps {
  paragraphId: string;
  onGenerateAudio: () => void;
}

/**
 * Small inline indicator showing audio status for a paragraph.
 * Green speaker = has narration audio (click to expand compact AudioPlayer).
 * Gray speaker = no audio (click to open GenerationModal in audio mode).
 */
export function ParagraphAudioIndicator({
  paragraphId,
  onGenerateAudio,
}: ParagraphAudioIndicatorProps) {
  const { data: audioFiles, isLoading } = useParagraphAudio(paragraphId);

  if (isLoading) return null;

  const hasAudio = audioFiles && audioFiles.length > 0;
  const latestAudio = hasAudio ? audioFiles[audioFiles.length - 1] : null;

  if (!hasAudio) {
    return (
      <Tooltip title="No audio narration. Click to generate.">
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onGenerateAudio();
          }}
          sx={{ opacity: 0.4 }}
          aria-label="Generate audio narration"
        >
          <VolumeOffIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title="Has audio narration">
        <VolumeUpIcon fontSize="small" color="success" />
      </Tooltip>
      {latestAudio && (
        <Box sx={{ minWidth: 180, maxWidth: 260 }}>
          <AudioPlayer src={latestAudio.url} compact />
        </Box>
      )}
    </Box>
  );
}
